import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import type { DocNode, DocumentsOperations, Team } from '../lib/types'
import { documentNameProblem } from '../lib/documentName'
import { DocIcon } from './DocIcon'
import { Icon, type IconName } from './Icon'
import { MediaLightbox } from './MediaLightbox'
import { useToast } from './Toast'

// ─── Documents tab ────────────────────────────────────────────────────────

type DocDialogState =
  | { kind: "new-folder" }
  | { kind: "upload" }
  | { kind: "rename"; target: string }
  | { kind: "delete"; target?: string }
  | { kind: "move" }
  | null;

function DocModal({ children, onCancel }: { children: ReactNode; onCancel: () => void }) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(10,20,15,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.surfaceContainerLowest, borderRadius: 10, width: "100%", maxWidth: 440,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)", overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function DocPrompt({
  title, subtitle, placeholder, initial = "", confirmLabel, validate, onCancel, onSubmit,
}: {
  title: string; subtitle?: string; placeholder: string; initial?: string;
  confirmLabel?: string;
  /** A message explaining why the (trimmed) value cannot be submitted, or null. */
  validate?: (val: string) => string | null;
  onCancel: () => void; onSubmit: (val: string) => void;
}) {
  const { t } = useTranslation('teams');
  const [val, setVal] = useState(initial);
  const invalid = val.trim() ? (validate?.(val.trim()) ?? null) : null;
  const canSubmit = !!val.trim() && !invalid;
  const submit = () => { if (canSubmit) onSubmit(val.trim()); };
  return (
    <DocModal onCancel={onCancel}>
      <div style={{ padding: "20px 22px 8px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 4 }}>{subtitle}</div>}
      </div>
      <div style={{ padding: "12px 22px 20px" }}>
        <input
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={placeholder}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={invalid ? "doc-prompt-error" : undefined}
          style={{
            width: "100%", padding: "10px 12px", fontSize: 14,
            border: `1.5px solid ${invalid ? COLORS.error : COLORS.surfaceContainerHighest}`, borderRadius: 6,
            outline: "none", background: COLORS.surfaceContainerLowest, color: COLORS.onSurface,
            boxSizing: "border-box",
          }}
        />
        {invalid && (
          <div id="doc-prompt-error" role="alert" style={{ fontSize: 13, color: COLORS.error, marginTop: 6 }}>
            {invalid}
          </div>
        )}
      </div>
      <div style={{
        display: "flex", gap: 8, justifyContent: "flex-end", padding: "14px 22px",
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surfaceContainer,
      }}>
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>{t('detail.documents.cancel')}</button>
        <button className="wh-btn wh-btn-primary" onClick={submit} disabled={!canSubmit}>{confirmLabel ?? t('detail.documents.save')}</button>
      </div>
    </DocModal>
  );
}

function DocConfirm({ title, body, onCancel, onConfirm }: {
  title: string; body: string; onCancel: () => void; onConfirm: () => void;
}) {
  const { t } = useTranslation('teams');
  return (
    <DocModal onCancel={onCancel}>
      <div style={{ padding: "20px 22px 8px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500 }}>{title}</div>
      </div>
      <div style={{ padding: "8px 22px 20px", fontSize: 14, color: COLORS.onSurface, lineHeight: 1.5 }}>
        {body}
      </div>
      <div style={{
        display: "flex", gap: 8, justifyContent: "flex-end", padding: "14px 22px",
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surfaceContainer,
      }}>
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>{t('detail.documents.cancel')}</button>
        <button
          onClick={onConfirm}
          style={{
            padding: "8px 16px", borderRadius: 6, border: "none",
            background: "#b3261e", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}
        >{t('detail.documents.delete')}</button>
      </div>
    </DocModal>
  );
}

function DocMovePicker({ tree, sourceIds, currentParent, onCancel, onConfirm }: {
  tree: Record<string, DocNode>; sourceIds: string[]; currentParent: string;
  onCancel: () => void; onConfirm: (dest: string) => void;
}) {
  const { t } = useTranslation('teams');
  const [dest, setDest] = useState(currentParent);

  const forbidden = new Set<string>();
  const walk = (id: string) => {
    forbidden.add(id);
    const n = tree[id];
    if (n?.type === "folder") n.children.forEach(walk);
  };
  sourceIds.forEach((id) => { if (tree[id]?.type === "folder") walk(id); });

  /**
   * A folder can receive these items only if it is neither one of the moved
   * folders (nor nested inside one) nor the folder they already live in.
   *
   * The current folder counts as no destination rather than as a selectable
   * row: moving items where they already are is not a move, so offering it
   * only leads to a confirm button that stays inert. In a team whose documents
   * are a single folder — one empty test folder, the shape a brand-new team
   * has — that left every "Move" click doing nothing whatsoever.
   */
  const isDestination = (id: string) => !forbidden.has(id) && id !== currentParent;
  const hasDestination = Object.values(tree)
    .some((n) => n.type === "folder" && isDestination(n.id));

  const renderFolder = (id: string, depth: number): ReactNode => {
    const n = tree[id];
    if (!n || n.type !== "folder") return null;
    const disabled = !isDestination(id);
    return (
      <Fragment key={id}>
        <button
          disabled={disabled}
          onClick={() => setDest(id)}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            width: "100%", padding: "10px 12px", paddingLeft: 12 + depth * 18,
            border: "none",
            background: dest === id ? COLORS.primaryOpacity8 : "transparent",
            borderLeft: dest === id ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            fontSize: 13, fontWeight: 600, textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.35 : 1, color: COLORS.onSurface,
          }}
        >
          <DocIcon kind="folder" size={22} />
          <span style={{ flex: 1 }}>{id === "root" ? t('detail.documents.root') : n.name}</span>
          {id === currentParent && (
            <span style={{ fontSize: 10, color: COLORS.outline, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {t('detail.documents.current')}
            </span>
          )}
        </button>
        {(n.children || []).map((c) => renderFolder(c, depth + 1))}
      </Fragment>
    );
  };

  return (
    <DocModal onCancel={onCancel}>
      <div style={{ padding: "20px 22px 12px" }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 20, fontWeight: 500 }}>{t('detail.documents.moveTitle')}</div>
        <div style={{ fontSize: 13, color: COLORS.outline, marginTop: 4 }}>
          {hasDestination
            ? t('detail.documents.moveChoose', { count: sourceIds.length })
            : t('detail.documents.moveNoDestination')}
        </div>
      </div>
      {hasDestination && (
        <div style={{ padding: "0 8px 12px", maxHeight: 320, overflowY: "auto" }}>
          {renderFolder("root", 0)}
        </div>
      )}
      <div style={{
        display: "flex", gap: 8, justifyContent: "flex-end", padding: "14px 22px",
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, background: COLORS.surfaceContainer,
      }}>
        <button className="wh-btn wh-btn-outline" onClick={onCancel}>{t('detail.documents.cancel')}</button>
        {/* No confirm when there is nowhere to move to: a button that cannot
            ever act is what made this read as "Move does nothing". */}
        {hasDestination && (
          <button className="wh-btn wh-btn-primary" onClick={() => onConfirm(dest)} disabled={!isDestination(dest)}>{t('detail.documents.move')}</button>
        )}
      </div>
    </DocModal>
  );
}

function DocMenuItem({ icon, label, danger = false, onClick }: {
  icon: IconName; label: string; danger?: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        width: "100%", padding: "8px 10px", borderRadius: 4,
        border: "none", background: "transparent", cursor: "pointer",
        fontSize: 13, fontWeight: 600, textAlign: "left",
        color: danger ? "#b3261e" : COLORS.onSurface,
      }}
    >
      <Icon name={icon} size={14} />
      <span>{label}</span>
    </button>
  );
}

function DocItemRow({
  item, last, selected, selectionMode, canEdit, openLabel, onOpen, onToggle, onMenu,
}: {
  item: DocNode; last: boolean; selected: boolean; selectionMode: boolean; canEdit: boolean;
  openLabel: string;
  onOpen: () => void; onToggle: () => void;
  onMenu: (action: "rename" | "delete" | "move" | "download") => void;
}) {
  const { t } = useTranslation('teams');
  const [menu, setMenu] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const isFolder = item.type === "folder";

  const openMenu = () => {
    if (menu) { setMenu(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const menuH = 180;
    const flipUp = window.innerHeight - r.bottom < menuH;
    setMenuPos({
      top: flipUp ? Math.max(8, r.top - 8 - menuH) : r.bottom + 4,
      left: Math.max(8, r.right - 200),
    });
    setMenu(true);
  };

  const activate = () => (selectionMode && canEdit) ? onToggle() : onOpen();

  return (
    <div
      // A row is the way a document is opened, so it has to be reachable
      // without a mouse. The nested menu button stops its own events, so it
      // never doubles as an activation of the row.
      role="button"
      tabIndex={0}
      aria-label={openLabel}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); }
      }}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        borderBottom: last ? "none" : `1px solid ${COLORS.surfaceContainerHighest}`,
        background: selected ? COLORS.primaryOpacity8 : COLORS.surfaceContainerLowest,
        cursor: "pointer", transition: "background 120ms",
      }}
    >
      {/* CLUB-175: selection drives bulk move/delete — hidden for view-only. */}
      {canEdit && (
      <div
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        style={{
          width: 18, height: 18, borderRadius: 2, flexShrink: 0,
          border: selected ? "none" : `2px solid ${COLORS.onSurfaceVariant}`,
          background: selected ? COLORS.primary : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
          opacity: selectionMode || selected ? 1 : 0.4,
          transition: "background 120ms, border-color 120ms",
        }}
      >
        {selected && <Icon name="check" size={12} color={COLORS.onPrimary} />}
      </div>
      )}

      <DocIcon kind={isFolder ? "folder" : item.kind} size={40} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.name}
        </div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {isFolder
            ? `${t('detail.documents.itemCount', { count: item.children?.length ?? 0 })} · ${item.updated || "—"} · ${t('detail.documents.by', { name: item.by || "—" })}`
            : `${item.size} · ${item.updated} · ${t('detail.documents.by', { name: item.by })}`}
        </div>
      </div>

      {/* CLUB-175: the only view-only action is Download (files); a folder row
          has no menu at all for view-only members. */}
      {(canEdit || !isFolder) && (
      <div onClick={(e) => e.stopPropagation()}>
        <button ref={btnRef} className="wh-btn-icon" aria-label={t('detail.documents.actionsFor', { name: item.name })} onClick={openMenu}>
          <Icon name="more" size={18} />
        </button>
        {menu && menuPos && (
          <>
            <div onClick={() => setMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 1000 }} />
            <div className="wh-card" style={{
              position: "fixed", top: menuPos.top, left: menuPos.left,
              zIndex: 1001, width: 200, padding: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}>
              {!isFolder && <DocMenuItem icon="download" label={t('detail.documents.download')} onClick={() => { setMenu(false); onMenu("download"); }} />}
              {canEdit && <DocMenuItem icon="edit" label={t('detail.documents.rename')} onClick={() => { setMenu(false); onMenu("rename"); }} />}
              {canEdit && <DocMenuItem icon="arrow-right" label={t('detail.documents.move')} onClick={() => { setMenu(false); onMenu("move"); }} />}
              {canEdit && <div style={{ height: 1, background: COLORS.surfaceContainerHighest, margin: "4px 0" }} />}
              {canEdit && <DocMenuItem icon="trash" label={t('detail.documents.delete')} danger onClick={() => { setMenu(false); onMenu("delete"); }} />}
            </div>
          </>
        )}
      </div>
      )}
    </div>
  );
}

function emptyDocRoot(): Record<string, DocNode> {
  return { root: { id: 'root', type: 'folder', name: 'Documents', parent: null, children: [] } };
}

export function DocumentsTab({ team, initialDocTree, ops }: {
  team: Team
  initialDocTree?: Record<string, DocNode>
  ops?: DocumentsOperations
}) {
  const [tree, setTree] = useState<Record<string, DocNode>>(
    () => JSON.parse(JSON.stringify(initialDocTree ?? emptyDocRoot())),
  );
  useEffect(() => {
    if (initialDocTree !== undefined) {
      setTree(JSON.parse(JSON.stringify(initialDocTree)))
      setCwd(c => (initialDocTree[c] ? c : 'root'))
    }
  }, [initialDocTree]);
  const [cwd, setCwd] = useState<string>("root");
  const [selection, setSelection] = useState<string[]>([]);
  const [dialog, setDialog] = useState<DocDialogState>(null);
  const [addMenu, setAddMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { showToast } = useToast()
  const { t } = useTranslation('teams')
  // A name is one segment of a Storage path, so a slash or a reserved name is
  // refused in the dialog rather than quietly becoming nested folders.
  const nameError = (name: string): string | null => {
    switch (documentNameProblem(name)) {
      case 'slash': return t('detail.documents.nameError.slash')
      case 'reserved': return t('detail.documents.nameError.reserved', { name: name.trim() })
      default: return null
    }
  }
  // Index into `previewImages` of the image open in the lightbox (null = closed).
  const [preview, setPreview] = useState<number | null>(null)
  const [previewFiles, setPreviewFiles] =
    useState<Array<{ url: string; name: string; kind: 'img' | 'pdf' }>>([])

  // CLUB-162/163: confirm bulk move/delete with a toast. Failures keep the
  // persistent inline banner below (it can show the genuine backend message —
  // name conflict, permission — which an auto-dismissing toast would lose).
  // CLUB-175: view-only members can browse & download but never mutate. Gate
  // every create/rename/move/delete affordance on the team's edit capability.
  const canEdit = team.canEdit ?? false

  /** Maps tree node IDs to relative storage paths ('root' → ''). */
  const toRel = (nodeId: string) => nodeId === 'root' ? '' : nodeId

  // If cwd was deleted (e.g. after refresh), fall back to root.
  const effectiveCwd = tree[cwd] ? cwd : 'root'
  const node = tree[effectiveCwd]
  const items = useMemo(() => {
    const list = (node.type === "folder" ? node.children : []).map((id) => tree[id]).filter(Boolean);
    list.sort((a, b) => (a.type === b.type ? a.name.localeCompare(b.name) : a.type === "folder" ? -1 : 1));
    return list;
  }, [node, tree]);

  const crumbs: DocNode[] = [];
  { let c: DocNode | undefined = node; while (c) { crumbs.unshift(c); c = c.parent ? tree[c.parent] : undefined; } }

  const go = (id: string) => { setCwd(id); setSelection([]); };
  const toggleSel = (id: string) => setSelection((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSel = () => setSelection([]);
  const allSelected = items.length > 0 && selection.length === items.length;
  const selectAll = () => setSelection(allSelected ? [] : items.map((i) => i.id));

  const updateTree = (fn: (t: Record<string, DocNode>) => void) =>
    setTree((t) => { const next = JSON.parse(JSON.stringify(t)); fn(next); return next; });

  const createFolder = async (name: string) => {
    if (ops) {
      setBusy(true); setError(null)
      try {
        await ops.createFolder(name, toRel(effectiveCwd))
        showToast(t('detail.documents.folderCreated', { name }))
      }
      catch (e: any) { setError(e.message || t('detail.documents.createFailed')) }
      finally { setBusy(false) }
    } else {
      const id = "f-" + Date.now()
      updateTree((t) => {
        t[id] = { id, type: "folder", name, parent: effectiveCwd, updated: "just now", by: "You", count: 0, children: [] }
        const cur = t[effectiveCwd]; if (cur.type === "folder") cur.children.push(id)
      })
    }
    setDialog(null)
  }

  const uploadFile = (name: string) => {
    // mock-only path: real upload uses the file input (handleFileInput)
    const id = "d-" + Date.now()
    const ext = (name.split(".").pop() || "").toLowerCase()
    const kind = ext === "pdf" ? "pdf" : ext === "xlsx" || ext === "xls" ? "xls" : ext === "png" || ext === "jpg" || ext === "jpeg" ? "img" : "doc"
    updateTree((t) => {
      t[id] = { id, type: "file", kind, name, parent: effectiveCwd, size: "—", updated: "just now", by: "You" }
      const cur = t[effectiveCwd]; if (cur.type === "folder") cur.children.push(id)
    })
    setDialog(null)
  }

  const renameItem = async (id: string, name: string) => {
    if (ops) {
      setBusy(true); setError(null)
      try { await ops.renameItem(toRel(id), name, tree[id]?.type === 'folder') }
      catch (e: any) { setError(e.message || t('detail.documents.renameFailed')) }
      finally { setBusy(false) }
    } else {
      updateTree((t) => { t[id].name = name; t[id].updated = "just now" })
    }
    setDialog(null)
  }

  const deleteItems = async (ids: string[]) => {
    const n = ids.length
    if (ops) {
      setBusy(true); setError(null)
      try {
        await ops.deleteItems(ids.map(id => ({ relPath: toRel(id), isFolder: tree[id]?.type === 'folder' })))
        showToast(t('detail.documents.deleted', { count: n }))
      }
      catch (e: any) { setError(e.message || t('detail.documents.deleteFailed')) }
      finally { setBusy(false) }
    } else {
      updateTree((t) => {
        const purge = (x: string) => {
          const nn = t[x]; if (!nn) return
          if (nn.type === "folder") nn.children.forEach(purge)
          if (nn.parent && t[nn.parent]) { const p = t[nn.parent]; if (p.type === "folder") p.children = p.children.filter((c) => c !== x) }
          delete t[x]
        }
        ids.forEach(purge)
      })
      showToast(t('detail.documents.deleted', { count: n }))
    }
    setSelection([]); setDialog(null)
  }

  const moveItems = async (ids: string[], destId: string) => {
    const n = ids.length
    const destName = destId === 'root' ? t('detail.documents.root') : tree[destId]?.name ?? ''
    if (ops) {
      setBusy(true); setError(null)
      try {
        await ops.moveItems(
          ids.map(id => ({ relPath: toRel(id), isFolder: tree[id]?.type === 'folder' })),
          toRel(destId),
        )
        showToast(t('detail.documents.moved', { count: n, folder: destName }))
      }
      catch (e: any) { setError(e.message || t('detail.documents.moveFailed')) }
      finally { setBusy(false) }
    } else {
      updateTree((t) => {
        ids.forEach((id) => {
          const nn = t[id]; if (!nn) return
          if (nn.parent && t[nn.parent]) { const p = t[nn.parent]; if (p.type === "folder") p.children = p.children.filter((c) => c !== id) }
          nn.parent = destId
          const dest = t[destId]; if (dest.type === "folder") dest.children.push(id)
        })
      })
      showToast(t('detail.documents.moved', { count: n, folder: destName }))
    }
    setSelection([]); setDialog(null)
  }

  /**
   * Saves the file under its own name. The bytes come back through the ops
   * seam rather than a plain link, because a link to the cross-origin storage
   * URL is ignored by the download attribute and renders the file instead.
   */
  const downloadFile = async (item: DocNode) => {
    if (!ops || item.type !== 'file') return
    setBusy(true); setError(null)
    try {
      const blob = await ops.getFileBlob(toRel(item.id))
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = item.name
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoked a tick later, not in a finally alongside this click. A browser
      // reads the object URL while it starts the save, and revoking in the same
      // task can cancel a save that has not begun reading yet — which costs the
      // user the file with no error to show for it.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
    } catch {
      setError(t('detail.documents.downloadFailed', { name: item.name }))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Opens a file in place when the browser can render it — images and PDFs
   * both preview in the overlay — and downloads anything it cannot.
   *
   * A PDF renders in a frame rather than a separate tab: sending the reader
   * out of the app to read a document that belongs to the team they are
   * looking at is not previewing it. The overlay keeps a link out for a
   * browser whose PDF plugin refuses the frame.
   */
  const openFile = async (item: DocNode) => {
    if (!ops || item.type !== 'file') return
    if (item.kind !== 'img' && item.kind !== 'pdf') { await downloadFile(item); return }
    setBusy(true); setError(null)
    try {
      // Resolve every previewable file in this folder, not just the clicked
      // one, so prev/next inside the overlay has somewhere to go.
      const previewable = items.filter(
        (i) => i.type === 'file' && (i.kind === 'img' || i.kind === 'pdf'),
      )
      const resolved = await Promise.all(
        previewable.map(async (i) => ({
          url: await ops.getFileUrl(toRel(i.id)),
          name: i.name,
          kind: (i.type === 'file' && i.kind === 'pdf' ? 'pdf' : 'img') as 'img' | 'pdf',
        })),
      )
      setPreviewFiles(resolved)
      setPreview(Math.max(0, previewable.findIndex((i) => i.id === item.id)))
    } catch {
      setError(t('detail.documents.openFailed', { name: item.name }))
    } finally { setBusy(false) }
  }

  const selCount = selection.length;
  const headerHasSel = selCount > 0;

  return (
    <div>
      {!headerHasSel ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 14 }}>
            {crumbs.map((c, i) => (
              <Fragment key={c.id}>
                {i > 0 && <Icon name="chevron-right" size={14} color={COLORS.outline} />}
                <button onClick={() => go(c.id)} style={{
                  padding: i === 0 ? "4px 8px 4px 0" : "4px 8px", borderRadius: 4, border: "none", background: "transparent",
                  fontSize: 14, fontWeight: i === crumbs.length - 1 ? 700 : 500,
                  color: i === crumbs.length - 1 ? COLORS.onSurface : COLORS.outline, cursor: "pointer",
                }}>
                  {c.id === "root" ? t('detail.documents.root') : c.name}
                </button>
              </Fragment>
            ))}
            <span style={{ marginLeft: 8, fontSize: 12, color: COLORS.outline }}>
              {t('detail.documents.itemCount', { count: items.length })}
            </span>
          </div>
          {canEdit && items.length > 0 && (
            <button className="wh-btn wh-btn-outline" disabled={busy} onClick={selectAll}>
              <Icon name="check" size={14} />{allSelected ? t('detail.documents.deselectAll') : t('detail.documents.selectAll')}
            </button>
          )}
          {canEdit && (
          <div style={{ position: "relative" }}>
            <button className="wh-btn wh-btn-primary" disabled={busy} onClick={() => setAddMenu((m) => !m)}>
              <Icon name="plus" size={16} />{t('detail.documents.add')}
            </button>
            {addMenu && (
              <>
                <div onClick={() => setAddMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 10 }} />
                <div className="wh-card" style={{
                  position: "absolute", top: "calc(100% + 6px)", right: 0,
                  zIndex: 11, minWidth: 200, padding: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                }}>
                  <DocMenuItem icon="plus" label={t('detail.documents.newFolder')} onClick={() => { setAddMenu(false); setDialog({ kind: "new-folder" }); }} />
                  <DocMenuItem icon="upload" label={t('detail.documents.uploadFile')} onClick={() => {
                    setAddMenu(false)
                    // Open the OS file picker; the actual upload is handled by
                    // the hidden input's onChange below. (No draft-mode ops →
                    // fall back to the placeholder dialog.)
                    if (ops) fileInputRef.current?.click()
                    else setDialog({ kind: "upload" })
                  }} />
                </div>
              </>
            )}
          </div>
          )}
        </div>
      ) : (
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", marginBottom: 14,
          background: COLORS.primary, color: COLORS.onPrimary, borderRadius: 8,
        }}>
          <button onClick={clearSel} className="wh-btn-icon" style={{ color: COLORS.onPrimary }}>
            <Icon name="close" size={18} />
          </button>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>{t('detail.documents.selected', { count: selCount })}</div>
          <button onClick={selectAll} style={{
            padding: "6px 12px", borderRadius: 4,
            border: "1px solid rgba(255,255,255,0.4)", background: "transparent", color: COLORS.onPrimary,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
          }}>{allSelected ? t('detail.documents.deselectAll') : t('detail.documents.selectAll')}</button>
          {canEdit && (
          <button disabled={busy} onClick={() => setDialog({ kind: "move" })} style={{
            padding: "6px 12px", borderRadius: 4, border: "none",
            background: "rgba(255,255,255,0.18)", color: COLORS.onPrimary,
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Icon name="arrow-right" size={14} />{t('detail.documents.move')}
          </button>
          )}
          {canEdit && (
          <button disabled={busy} onClick={() => setDialog({ kind: "delete" })} style={{
            padding: "6px 12px", borderRadius: 4, border: "none",
            background: "#b3261e", color: "#fff",
            fontSize: 12, fontWeight: 600, cursor: "pointer",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Icon name="trash" size={14} />{t('detail.documents.delete')}
          </button>
          )}
        </div>
      )}

      {error && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', marginBottom: 12,
          background: '#fdecea', color: '#b3261e', borderRadius: 8, fontSize: 13,
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b3261e', padding: 0 }}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}

      <div style={{ fontSize: 13, color: COLORS.outline, marginBottom: 12 }}>
        {t('detail.documents.sharedWith', { count: team.members, team: team.name })}
      </div>

      <div className="wh-card" style={{ overflow: "hidden" }}>
        {items.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: COLORS.outline }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t('detail.documents.empty')}</div>
            {canEdit && <div style={{ fontSize: 13 }}>{t('detail.documents.emptyHint')}</div>}
          </div>
        ) : (
          items.map((item, i) => (
            <DocItemRow
              key={item.id}
              item={item}
              last={i === items.length - 1}
              selected={selection.includes(item.id)}
              selectionMode={headerHasSel}
              canEdit={canEdit}
              openLabel={t('detail.documents.open', { name: item.name })}
              onOpen={() => { if (item.type === "folder") go(item.id); else void openFile(item); }}
              onToggle={() => toggleSel(item.id)}
              onMenu={(action) => {
                if (action === "rename") setDialog({ kind: "rename", target: item.id });
                else if (action === "delete") setDialog({ kind: "delete", target: item.id });
                else if (action === "move") { setSelection([item.id]); setDialog({ kind: "move" }); }
                else if (action === "download") void downloadFile(item);
              }}
            />
          ))
        )}
      </div>

      {dialog?.kind === "new-folder" && (
        <DocPrompt title={t('detail.documents.newFolder')} placeholder={t('detail.documents.folderPlaceholder')} confirmLabel={t('detail.documents.create')} validate={nameError} onCancel={() => setDialog(null)} onSubmit={createFolder} />
      )}
      {dialog?.kind === "upload" && (
        <DocPrompt title={t('detail.documents.uploadFile')} subtitle={t('detail.documents.uploadMockHint')} placeholder={t('detail.documents.filePlaceholder')} confirmLabel={t('detail.documents.upload')} onCancel={() => setDialog(null)} onSubmit={uploadFile} />
      )}
      {dialog?.kind === "rename" && dialog.target && (
        <DocPrompt title={t('detail.documents.rename')} initial={tree[dialog.target]?.name} placeholder="" confirmLabel={t('detail.documents.save')} validate={nameError} onCancel={() => setDialog(null)} onSubmit={(name) => renameItem(dialog.target!, name)} />
      )}
      {dialog?.kind === "delete" && (
        <DocConfirm
          title={dialog.target ? t('detail.documents.deleteOneTitle') : t('detail.documents.deleteManyTitle', { count: selCount })}
          body={dialog.target
            ? t(tree[dialog.target]?.type === "folder" ? 'detail.documents.deleteFolderBody' : 'detail.documents.deleteFileBody', { name: tree[dialog.target]?.name ?? '' })
            : t('detail.documents.deleteManyBody')}
          onCancel={() => setDialog(null)}
          onConfirm={() => deleteItems(dialog.target ? [dialog.target] : selection)}
        />
      )}
      {dialog?.kind === "move" && (
        <DocMovePicker
          tree={tree}
          sourceIds={selection}
          currentParent={cwd}
          onCancel={() => setDialog(null)}
          onConfirm={(dest) => moveItems(selection, dest)}
        />
      )}
      {preview !== null && previewFiles[preview] && (
        <MediaLightbox
          items={previewFiles.map((i) => ({
            url: i.url,
            type: i.kind === 'pdf' ? ('pdf' as const) : ('image' as const),
          }))}
          index={preview}
          labels={{
            title: t('detail.documents.preview.title'),
            close: t('detail.documents.preview.close'),
            previous: t('detail.documents.preview.previous'),
            next: t('detail.documents.preview.next'),
            openInNewTab: t('detail.documents.preview.openInNewTab'),
          }}
          onClose={() => setPreview(null)}
          onNavigate={setPreview}
          onItemError={(i) => {
            // A URL that resolved and then would not load — the shape the
            // legacy decomposed-Unicode names take, where the token is issued
            // for a name the storage layer then fails to match. Say so instead
            // of leaving an empty frame open.
            setError(t('detail.documents.openFailed', { name: previewFiles[i]?.name ?? '' }))
            setPreview(null)
          }}
        />
      )}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={() => {
          const input = fileInputRef.current
          const files = input?.files
          if (!files || files.length === 0 || !ops) return
          // Read the count now: ops.uploadFiles reads the FileList after an
          // await, and clearing input.value empties that same live FileList, so
          // the value reset must wait until the upload has finished (.finally).
          // Doing it before (the previous native-listener workaround did) left
          // uploadFiles with an empty list and silently uploaded nothing.
          const count = files.length
          setBusy(true); setError(null)
          ops.uploadFiles(files, toRel(effectiveCwd))
            .then(() => showToast(t('detail.documents.uploaded', { count })))
            .catch((e: any) => { setError(e.message || t('detail.documents.uploadFailed')) })
            .finally(() => { setBusy(false); if (fileInputRef.current) fileInputRef.current.value = '' })
        }}
      />
    </div>
  );
}
